import { useCallback, useState, useRef, useEffect } from 'react';
import {
  closestCenter,
  pointerWithin,
  rectIntersection,
  getFirstCollision,  
  MeasuringStrategy,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

/**
 * Hook for handling nested sortable items with dnd-kit
 * 
 * @param {Array} items - Array of parent items, each with an id and a configurable subitems array
 * @param {Function} setItems - State setter function for items
 * @param {Object} options - Configuration options
 * @param {string} options.subItemsKey - Key name for nested items (default: 'subitems')
 * @returns {Object} DndContext props and helper functions
 */
export function useNestedSortable(items, setItems, options = {}) {
  const { subItemsKey = 'subitems' } = options;
  
  const [activeId, setActiveId] = useState(null);
  const [clonedItems, setClonedItems] = useState(null);
  const lastOverId = useRef(null);
  const recentlyMovedToNewContainer = useRef(false);

  const findParentItem = useCallback((id) => {
    return items.find(item => item.id === id);
  }, [items]);

  const findParentOfChild = useCallback((childId) => {
    return items.find(item => 
      item[subItemsKey]?.some(subItem => subItem.id === childId)
    );
  }, [items, subItemsKey]);

  const findContainer = useCallback((id) => {
    const parentItem = findParentItem(id);
    if (parentItem) {
      return parentItem.id;
    }
    const parentOfChild = findParentOfChild(id);
    return parentOfChild?.id;
  }, [findParentItem, findParentOfChild]);

  const isParentItem = useCallback((id) => {
    return findParentItem(id) !== undefined;
  }, [findParentItem]);

  const collisionDetection = useCallback((args) => {
    if (activeId && isParentItem(activeId)) {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter(
          (container) => isParentItem(container.id)
        ),
      });
    }

    const pointerIntersections = pointerWithin(args);
    const intersections =
      pointerIntersections.length > 0
        ? pointerIntersections
        : rectIntersection(args);
    
    let overId = getFirstCollision(intersections, 'id');

    if (overId != null) {
      const parentItem = findParentItem(overId);
      
      if (parentItem && parentItem[subItemsKey]?.length > 0) {
        const childIds = parentItem[subItemsKey].map(item => item.id);
        overId = closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            (container) =>
              container.id !== overId &&
              childIds.includes(container.id)
          ),
        })[0]?.id;
      }

      lastOverId.current = overId;
      return [{ id: overId }];
    }

    if (recentlyMovedToNewContainer.current) {
      lastOverId.current = activeId;
    }

    return lastOverId.current ? [{ id: lastOverId.current }] : [];
  }, [activeId, items, subItemsKey, isParentItem, findParentItem]);

  const onDragStart = useCallback(({ active }) => {
    setActiveId(active.id);
    setClonedItems(JSON.parse(JSON.stringify(items)));
  }, [items]);

  const onDragOver = useCallback(({ active, over }) => {
    const overId = over?.id;

    if (overId == null || isParentItem(active.id)) {
      return;
    }

    const overContainer = findContainer(overId);
    const activeContainer = findContainer(active.id);

    if (!overContainer || !activeContainer) {
      return;
    }

    if (activeContainer !== overContainer) {
      setItems((currentItems) => {
        const activeParent = currentItems.find(item => item.id === activeContainer);
        const overParent = currentItems.find(item => item.id === overContainer);

        if (!activeParent || !overParent) {
          return currentItems;
        }

        if (!Array.isArray(activeParent[subItemsKey]) || !Array.isArray(overParent[subItemsKey])) {
          return currentItems;
        }

        const activeChild = activeParent[subItemsKey].find(item => item.id === active.id);
        if (!activeChild) {
          return currentItems;
        }

        const overIndex = overParent[subItemsKey].findIndex(item => item.id === overId);        

        let newIndex;

        if (isParentItem(overId)) {
          newIndex = overParent[subItemsKey].length;
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top >
              over.rect.top + over.rect.height;

          const modifier = isBelowOverItem ? 1 : 0;
          newIndex = overIndex >= 0 ? overIndex + modifier : overParent[subItemsKey].length;
        }

        recentlyMovedToNewContainer.current = true;

        return currentItems.map(item => {
          if (item.id === activeContainer) {
            return {
              ...item,
              [subItemsKey]: item[subItemsKey].filter(subItem => subItem.id !== active.id)
            };
          }
          if (item.id === overContainer) {
            const newSubItems = [...(item[subItemsKey] || [])];
            newSubItems.splice(newIndex, 0, activeChild);
            return {
              ...item,
              [subItemsKey]: newSubItems
            };
          }
          return item;
        });
      });
    }
  }, [findContainer, isParentItem, subItemsKey, setItems]);

  const getParent = useCallback((id, itemsToSearch = items) => {
    const parentItem = itemsToSearch.find(item => item.id === id);
    if (parentItem) {
      return parentItem;
    }
    const parentOfChild = itemsToSearch.find(item => 
      item[subItemsKey]?.some(subItem => subItem.id === id)
    );
    return parentOfChild || null;
  }, [items, subItemsKey]);

  const buildActiveOverInfo = useCallback((activeId, overId, itemsToSearch) => {
    const activeIsParent = isParentItem(activeId);
    const overIsParent = isParentItem(overId);
    
    const activeParent = activeIsParent ? null : getParent(activeId, clonedItems || itemsToSearch);
    const overParent = overIsParent ? null : getParent(overId, itemsToSearch);    
    
    return {
      active: {
        id: activeId,
        isParent: activeIsParent,
        parent: activeParent || undefined,
      },
      over: {
        id: overId,
        isParent: overIsParent,
        parent: overParent || undefined,
      },
    };
  }, [isParentItem, clonedItems, getParent]);

  const onDragEnd = useCallback(({ active, over }) => {
    if (!over) {
      setActiveId(null);
      return;
    }

    if (isParentItem(active.id)) {
      const activeIndex = items.findIndex(item => item.id === active.id);
      const overIndex = items.findIndex(item => item.id === over.id);

      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        const newItems = arrayMove(items, activeIndex, overIndex);
        setItems(newItems);
        if (options.onDragEnd) {
          const info = buildActiveOverInfo(active.id, over.id, newItems);
          options.onDragEnd({ ...info, items: newItems });
        }        
      }
      setActiveId(null);
      return;
    }

    const activeContainer = findContainer(active.id);
    if (!activeContainer) {
      setActiveId(null);
      return;
    }

    const overId = over.id;
    const overContainer = findContainer(overId);

    if (!overContainer) {
      setActiveId(null);
      return;
    }

    if (activeContainer === overContainer) {
      setItems((currentItems) => {
        const parent = currentItems.find(item => item.id === activeContainer);
        if (parent && Array.isArray(parent[subItemsKey])) {
          const activeIndex = parent[subItemsKey].findIndex(item => item.id === active.id);
          const overIndex = parent[subItemsKey].findIndex(item => item.id === overId);

          if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
            const newItems = currentItems.map(item => {
              if (item.id === activeContainer) {
                return {
                  ...item,
                  [subItemsKey]: arrayMove(item[subItemsKey], activeIndex, overIndex)
                };
              }
              return item;
            });
            if (options.onDragEnd) {
              const info = buildActiveOverInfo(active.id, overId, newItems);
              options.onDragEnd({ ...info, items: newItems });
            }
            return newItems;
          }
        }
        if (options.onDragEnd) {
          const info = buildActiveOverInfo(active.id, overId, currentItems);
          options.onDragEnd({ ...info, items: currentItems });
        }
        return currentItems;
      });
    } else {
      setItems((currentItems) => {
        const activeParent = currentItems.find(item => item.id === activeContainer);
        const overParent = currentItems.find(item => item.id === overContainer);
        
        if (activeParent && overParent && Array.isArray(activeParent[subItemsKey]) && Array.isArray(overParent[subItemsKey])) {
          const activeChild = activeParent[subItemsKey].find(item => item.id === active.id);
          if (activeChild) {
            const newItems = currentItems.map(item => {
              if (item.id === activeContainer) {
                return {
                  ...item,
                  [subItemsKey]: item[subItemsKey].filter(subItem => subItem.id !== active.id)
                };
              }
              if (item.id === overContainer) {
                const newSubItems = [...(item[subItemsKey] || [])];
                const insertIndex = isParentItem(overId) ? newSubItems.length : newSubItems.findIndex(sub => sub.id === overId);
                newSubItems.splice(insertIndex >= 0 ? insertIndex : newSubItems.length, 0, activeChild);
                return {
                  ...item,
                  [subItemsKey]: newSubItems
                };
              }
              return item;
            });
            if (options.onDragEnd) {
              const info = buildActiveOverInfo(active.id, overId, newItems);
              options.onDragEnd({ ...info, items: newItems });
            }
            return newItems;
          }
        }
        if (options.onDragEnd) {
          const info = buildActiveOverInfo(active.id, overId, currentItems);
          options.onDragEnd({ ...info, items: currentItems });
        }
        return currentItems;
      });
    }

    setActiveId(null);
  }, [items, setItems, findContainer, isParentItem, subItemsKey, options, buildActiveOverInfo]);

  const onDragCancel = useCallback(() => {
    if (clonedItems) {
      setItems(clonedItems);
    }
    setActiveId(null);
    setClonedItems(null);
  }, [clonedItems, setItems]);

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  const getParentIds = useCallback(() => {
    return items.map(item => item.id);
  }, [items]);

  const getChildIds = useCallback((parentId) => {
    const parent = findParentItem(parentId);
    return parent?.[subItemsKey]?.map(item => item.id) || [];
  }, [findParentItem, subItemsKey]);


  const meassuring = {
    droppable: {
      strategy: MeasuringStrategy.Always,
    },
  };

  return {
    collisionDetection,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDragCancel,
    activeId,
    isParentItem,
    getParentIds,
    getChildIds,
    meassuring,
  };
}

